import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdminModuleSlide, useCreateSlide, useUpdateSlide } from '@/hooks/useEducationAdmin';
import WysiwygEditor from '@/components/admin/WysiwygEditor';

const slideSchema = z.object({
  title: z.string().min(1, "Title is required").max(200, "Title must be less than 200 characters"),
  content: z.string().optional(),
  slide_type: z.enum(['content', 'image', 'video']),
  media_url: z.string().url("Must be a valid URL").optional().or(z.literal("")),
  sort_order: z.number().min(0),
  footnotes: z.string().optional(),
});

type SlideFormData = z.infer<typeof slideSchema>;

interface SlideEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slide: AdminModuleSlide | null;
  moduleId: string;
}

export const SlideEditDialog: React.FC<SlideEditDialogProps> = ({
  open,
  onOpenChange,
  slide,
  moduleId,
}) => {
  const createSlide = useCreateSlide();
  const updateSlide = useUpdateSlide();
  const isEditing = !!slide;

  const form = useForm<SlideFormData>({
    resolver: zodResolver(slideSchema),
    defaultValues: {
      title: '',
      content: '',
      slide_type: 'content',
      media_url: '',
      sort_order: 0,
      footnotes: '',
    },
  });

  React.useEffect(() => {
    if (slide) {
      form.reset({
        title: slide.title,
        content: slide.content || '',
        slide_type: slide.slide_type,
        media_url: slide.media_url || '',
        sort_order: slide.sort_order,
        footnotes: slide.footnotes || '',
      });
    } else {
      form.reset({
        title: '',
        content: '',
        slide_type: 'content',
        media_url: '',
        sort_order: 0,
        footnotes: '',
      });
    }
  }, [slide, form]);

  const onSubmit = async (data: SlideFormData) => {
    try {
      if (isEditing) {
        await updateSlide.mutateAsync({
          id: slide.id,
          ...data,
        });
      } else {
        await createSlide.mutateAsync({
          module_id: moduleId,
          ...data,
        });
      }
      onOpenChange(false);
      form.reset();
    } catch (error) {
      // Error handling is done in the mutation hooks
    }
  };

  const isLoading = createSlide.isPending || updateSlide.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Slide' : 'Create New Slide'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the slide information below.'
              : 'Create a new slide for this module.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Slide Title</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Introduction to Bitcoin" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slide_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Slide Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select slide type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="content">Content</SelectItem>
                        <SelectItem value="image">Image</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content</FormLabel>
                  <FormControl>
                    <WysiwygEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Enter the slide content..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="media_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Media URL (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="https://..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sort_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sort Order</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="footnotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Footnotes (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional notes or references for this slide..."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : isEditing ? 'Update Slide' : 'Create Slide'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};